import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ReceiptText, ShieldCheck, Target, ArrowUpRight, TrendingUp } from "lucide-react";
import { PageBody, PageHeader } from "@/components/AppLayout";
import { boardMetrics, listExpenses } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import StatusPill from "@/components/StatusPill";
import { DASHBOARD, EXPENSE } from "@/constants/testIds";

const PLATFORM_ROLES = ["super_admin", "regional_admin", "financials_admin"];

function money(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function Overview() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [recent, setRecent] = useState([]);

  const isPlatform = user && PLATFORM_ROLES.includes(user.role);

  useEffect(() => {
    if (!user || isPlatform) return;
    async function load() {
      try {
        if (["admin", "treasurer", "board", "organization_admin", "account_admin", "supervisor", "accountant"].includes(user.role)) {
          const m = await boardMetrics();
          setMetrics(m);
          setRecent(m.recent_expenses || []);
        } else {
          const list = await listExpenses({ mine: true });
          setRecent(list.slice(0, 8));
        }
      } catch {
        /* ignore */
      }
    }
    load();
  }, [user, isPlatform]);

  // Platform users skip the org dashboard
  if (isPlatform) return <Navigate to="/mission-control" replace />;

  return (
    <>
      <PageHeader title={`Welcome, ${user?.full_name?.split(" ")[0] || "friend"}.`} subtitle="A quiet pulse of your organization's stewardship.">
        <Link
          to="/app/expenses/new"
          data-testid={EXPENSE.newButton}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90"
        >
          New expense <ArrowUpRight size={14} />
        </Link>
      </PageHeader>
      <PageBody>
        {metrics ? (
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4 mb-10">
            <MetricCard testId={DASHBOARD.totalBudget} icon={Target} label="Total budget" value={money(metrics.total_budget)} />
            <MetricCard testId={DASHBOARD.totalSpent} icon={TrendingUp} label="Total spent" value={money(metrics.total_spent)} sub={`${((metrics.total_spent / Math.max(metrics.total_budget, 1)) * 100).toFixed(0)}% utilized`} />
            <MetricCard testId={DASHBOARD.pendingAmount} icon={ShieldCheck} label="Awaiting approval" value={money(metrics.pending_amount)} sub={`${metrics.pending_count} expenses`} />
            <MetricCard testId={DASHBOARD.reimbursableAmount} icon={ReceiptText} label="To reimburse" value={money(metrics.reimbursable_amount)} sub={`${metrics.approved_count} approved`} />
          </div>
        ) : null}

        <div className="border border-border bg-card rounded-md">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <div>
              <div className="serif text-lg text-primary">Recent activity</div>
              <div className="text-xs text-muted-foreground mt-0.5">Last 8 expenses</div>
            </div>
            <Link to="/app/expenses" className="text-xs text-muted-foreground hover:text-foreground under-link">View all →</Link>
          </div>
          {recent.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <div className="serif text-xl text-primary">No expenses yet</div>
              <p className="text-sm text-muted-foreground mt-2">Submit your first receipt to start the ledger.</p>
              <Link
                to="/app/expenses/new"
                className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm"
              >
                Submit an expense <ArrowUpRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {recent.map((e) => (
                <Link
                  key={e.id}
                  to={`/app/expenses?focus=${e.id}`}
                  className="px-6 py-4 flex items-center gap-6 hover:bg-accent/40 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{e.vendor}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      {e.program_name} · {e.category} · by {e.submitted_by_name}
                    </div>
                  </div>
                  <div className="mono text-sm text-primary tabular shrink-0">{money(e.amount)}</div>
                  <div className="shrink-0"><StatusPill status={e.status} /></div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </PageBody>
    </>
  );
}

function MetricCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div data-testid={testId} className="border border-border bg-card rounded-md p-6">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <Icon size={13} className="text-secondary" /> {label}
      </div>
      <div className="serif text-3xl mt-3 text-primary tabular">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}
